import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import PaymentsIcon from '@mui/icons-material/Payments';
import HistoryIcon from '@mui/icons-material/History';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import PageHeader from '../common/PageHeader';
import SearchField from '../common/SearchField';
import {
  useGetOutstandingPageMutation,
  useLazyGetOpenBillsQuery,
  useLazyGetPaymentHistoryQuery,
  useReceiveSplitPaymentMutation,
} from '../../api/reports/ReportApi';
import { Bill } from '../../types/bill';
import { OutstandingSummary, ReceiptLine, ReceivePayment } from '../../types/reports';
import { useSnackbar } from '../../utils/snackbar';
import { digitsOnly } from '../../utils/clean';
import { can } from '../../utils/auth';
import { invoiceDate } from '../../utils/dates';

const money = (value?: number | null) => Number(value || 0).toFixed(2);
const today = () => new Date().toISOString().slice(0, 10);

/**
 * Customer Dues - who still owes the shop money, and taking it when they come back.
 *
 * <p>Called "dues" rather than "outstanding" because that is the word used across
 * the counter. One list of people on the left, their unpaid bills on the right, and
 * a payment box that shows what is left as it is typed.
 */
const Outstanding = () => {
  const { openSnackbar } = useSnackbar();
  const [searchValue, setSearchValue] = useState('');

  const [customers, setCustomers] = useState<OutstandingSummary[]>([]);
  const [rowCount, setRowCount] = useState(0);
  /** Totals for the whole search, sent with the page - not a sum of what is shown. */
  const [totals, setTotals] = useState({ due: 0, bills: 0 });
  const [loading, setLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const timeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [getOutstandingPage] = useGetOutstandingPageMutation();

  /** One page of who owes, from the server - the same grid the other lists use. */
  const load = () => {
    if (timeout.current) clearTimeout(timeout.current);
    setLoading(true);
    timeout.current = setTimeout(async () => {
      try {
        const response = await getOutstandingPage({
          searchValue,
          page: paginationModel.page,
          size: paginationModel.pageSize,
        }).unwrap();
        setCustomers(response?.data || []);
        setRowCount(response?.totalCount || 0);
        setTotals({ due: Number(response?.totalDue || 0), bills: Number(response?.totalBills || 0) });
      } catch {
        setCustomers([]);
        setRowCount(0);
      } finally {
        setLoading(false);
      }
    }, 500);
  };

  useEffect(() => {
    load();
    return () => {
      if (timeout.current) clearTimeout(timeout.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue, paginationModel.page, paginationModel.pageSize]);

  /**
   * Any change of filter puts the list back on the first page.
   *
   * <p>Page 4 of the old results is not page 4 of the new ones - usually it is past
   * the end of them, and the shop is shown an empty grid for a search that matched.
   */
  useEffect(() => {
    setPaginationModel((model) => (model.page === 0 ? model : { ...model, page: 0 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue]);

  const [selected, setSelected] = useState<OutstandingSummary | null>(null);
  const [openBills, setOpenBills] = useState<Bill[]>([]);
  const [getOpenBills] = useLazyGetOpenBillsQuery();

  const [payTarget, setPayTarget] = useState<Bill | null>(null);
  /**
   * How the customer is settling up, one line per way of paying.
   *
   * <p>"Half in hand, half to the bank account" is an ordinary afternoon, and it is
   * one receipt, not two visits. One line is the usual case and looks exactly like a
   * single amount and mode; a second line appears only when somebody asks for it.
   */
  const [lines, setLines] = useState<{ paymentMode: ReceiptLine['paymentMode']; amount: string }[]>([
    { paymentMode: 'CASH', amount: '' },
  ]);
  const [paymentDate, setPaymentDate] = useState(today());
  const [remarks, setRemarks] = useState('');
  const [receiveSplitPayment, { isLoading: receiving }] = useReceiveSplitPaymentMutation();

  /** What the lines add up to, and what the bill would still be short by. */
  const entered = lines.reduce((sum, line) => sum + (Number(line.amount) || 0), 0);
  const stillShort = Math.max(Number(payTarget?.balanceAmount || 0) - entered, 0);
  const overBalance = entered > Number(payTarget?.balanceAmount || 0) + 0.001;

  const [historyFor, setHistoryFor] = useState<Bill | null>(null);
  const [history, setHistory] = useState<ReceivePayment[]>([]);
  const [getHistory] = useLazyGetPaymentHistoryQuery();

  const loadBills = async (row: OutstandingSummary) => {
    try {
      // preferCacheValue false, so a payment made a second ago is reflected here
      // rather than the list coming back from cache unchanged.
      return await getOpenBills({ customerName: row.customerName, mobileNumber: row.mobileNumber || '' }, false).unwrap();
    } catch {
      return [];
    }
  };

  const openCustomer = async (row: OutstandingSummary) => {
    setSelected(row);
    setOpenBills(await loadBills(row));
  };

  const openPay = (bill: Bill) => {
    setPayTarget(bill);
    // Opens on the whole balance in cash - what happens most of the time.
    setLines([{ paymentMode: 'CASH', amount: String(bill.balanceAmount ?? '') }]);
    setPaymentDate(today());
    setRemarks('');
  };

  const setLine = (index: number, patch: Partial<{ paymentMode: ReceiptLine['paymentMode']; amount: string }>) =>
    setLines((previous) => previous.map((line, at) => (at === index ? { ...line, ...patch } : line)));

  /**
   * A second way of paying, pre-filled with whatever is still short.
   *
   * <p>Somebody splitting a payment already knows the first figure; the second is
   * arithmetic, and getting it wrong is how a receipt ends up not adding up.
   */
  const addLine = () => {
    const short = Math.max(Number(payTarget?.balanceAmount || 0) - entered, 0);
    const used = new Set(lines.map((line) => line.paymentMode));
    const mode: ReceiptLine['paymentMode'] = used.has('CASH') ? 'UPI_CARD' : 'CASH';
    setLines((previous) => [...previous, { paymentMode: mode, amount: short > 0 ? String(short) : '' }]);
  };

  const removeLine = (index: number) => setLines((previous) => previous.filter((_line, at) => at !== index));

  const confirmPay = async () => {
    if (!payTarget?.id) return;

    const filled = lines.filter((line) => Number(line.amount) > 0);
    if (filled.length === 0) {
      openSnackbar('Enter an amount greater than zero', 'warning');
      return;
    }
    if (filled.length !== lines.length) {
      openSnackbar('Every payment line needs an amount, or remove it', 'warning');
      return;
    }
    const value = entered;
    if (value > Number(payTarget.balanceAmount || 0)) {
      openSnackbar(`That bill only has ${money(payTarget.balanceAmount)} left to pay`, 'warning');
      return;
    }
    try {
      const saved = await receiveSplitPayment({
        billId: payTarget.id,
        paymentDate,
        remarks,
        lines: filled.map((line) => ({ paymentMode: line.paymentMode, amount: Number(line.amount) })),
      }).unwrap();
      // Every line carries the position after the whole receipt, so any of them will do.
      const result = saved[saved.length - 1];
      openSnackbar(
        filled.length === 1
          ? `Received ${money(value)}. Balance ${money(result?.balanceAmount)}`
          : `Received ${money(value)} in ${filled.length} payments. Balance ${money(result?.balanceAmount)}`,
        'success',
      );
      setPayTarget(null);

      // The bill on screen is updated from what came back, so a part payment shows
      // its new balance immediately rather than the figure from before the payment.
      setOpenBills((previous) =>
        previous
          .map((bill) =>
            bill.id === payTarget.id
              ? {
                  ...bill,
                  paidAmount: Number(bill.paidAmount || 0) + value,
                  balanceAmount: Number(result.balanceAmount ?? 0),
                }
              : bill,
          )
          .filter((bill) => Number(bill.balanceAmount || 0) > 0),
      );

      load();
      if (selected) setOpenBills(await loadBills(selected));
    } catch (error: any) {
      openSnackbar(error?.data?.message || 'Could not record the payment', 'error');
    }
  };

  const openHistory = async (bill: Bill) => {
    setHistoryFor(bill);
    try {
      setHistory(await getHistory(bill.id as number).unwrap());
    } catch {
      setHistory([]);
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'customerName',
      headerName: 'Customer',
      flex: 1.4,
      minWidth: 170,
      renderCell: (row) => (
        <Stack direction="row" alignItems="center" gap={1}>
          <Avatar sx={{ width: 28, height: 28, fontSize: 13, bgcolor: 'action.hover', color: 'text.secondary' }}>
            {String(row.value ?? '?').charAt(0).toUpperCase()}
          </Avatar>
          <Typography variant="body2" fontWeight={600} noWrap>
            {String(row.value ?? '')}
          </Typography>
        </Stack>
      ),
    },
    { field: 'mobileNumber', headerName: 'Mobile', flex: 1, minWidth: 130 },
    { field: 'billCount', headerName: 'Bills', flex: 0.6, minWidth: 80, align: 'center', headerAlign: 'center' },
    {
      field: 'outstanding',
      headerName: 'Due',
      flex: 1,
      minWidth: 110,
      align: 'right',
      headerAlign: 'right',
      renderCell: (row) => (
        <Typography variant="body2" fontWeight={700} color="error.main">
          {money(row.value as number)}
        </Typography>
      ),
    },
  ];

  const receivedSoFar = history.reduce((sum, payment) => sum + Number(payment.amount || 0), 0);

  return (
    <Box>
      <PageHeader title="Customer Dues" subtitle="Money the shop is still owed on credit bills" />

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6} md={3}>
          <Card sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">
              TOTAL DUE
            </Typography>
            <Typography variant="h5" fontWeight={700} color="error.main">
              {money(totals.due)}
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">
              CUSTOMERS
            </Typography>
            <Typography variant="h5" fontWeight={700}>
              {rowCount}
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">
              UNPAID BILLS
            </Typography>
            <Typography variant="h5" fontWeight={700}>
              {totals.bills}
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">
              AVERAGE PER CUSTOMER
            </Typography>
            <Typography variant="h5" fontWeight={700}>
              {money(rowCount ? totals.due / rowCount : 0)}
            </Typography>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={2} alignItems="flex-start">
        {/* ------------------------------------------------ who owes */}
        <Grid item xs={12} md={6}>
          <Card sx={{ p: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
              <Typography variant="subtitle2" fontWeight={700} color="primary.main">
                WHO OWES
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {rowCount} customer(s)
              </Typography>
            </Stack>
            <SearchField value={searchValue} onChange={setSearchValue} placeholder="Any words: customer name or mobile..." width="100%" />

            <Box sx={{ mt: 1.5 }}>
              <DataGrid
                columns={columns}
                rows={customers}
                getRowId={(row) => `${row.customerName}|${row.mobileNumber ?? ''}`}
                rowCount={rowCount}
                paginationMode="server"
                paginationModel={paginationModel}
                onPaginationModelChange={(model: GridPaginationModel) => setPaginationModel(model)}
                pageSizeOptions={[10, 25, 50, 100]}
                disableRowSelectionOnClick
                loading={loading}
                onRowClick={(params) => openCustomer(params.row as OutstandingSummary)}
                sx={{ height: '58vh', cursor: 'pointer' }}
              />
            </Box>
          </Card>
        </Grid>

        {/* --------------------------------------------- their bills */}
        <Grid item xs={12} md={6}>
          <Card sx={{ p: 2 }}>
            {!selected ? (
              <Stack alignItems="center" sx={{ py: 6 }}>
                <Typography color="text.secondary" variant="body2">
                  Pick a customer on the left to see their unpaid bills.
                </Typography>
              </Stack>
            ) : (
              <>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={1}>
                  <Box>
                    <Typography fontWeight={700}>{selected.customerName}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {selected.mobileNumber || 'no mobile on file'}
                    </Typography>
                  </Box>
                  <Box textAlign="right">
                    <Typography variant="caption" color="text.secondary">
                      OWES
                    </Typography>
                    <Typography variant="h6" fontWeight={700} color="error.main">
                      {money(selected.outstanding)}
                    </Typography>
                  </Box>
                </Stack>
                <Divider sx={{ my: 1.5 }} />

                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Bill</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell align="right">Total</TableCell>
                      <TableCell align="right">Paid</TableCell>
                      <TableCell align="right">Balance</TableCell>
                      <TableCell align="center">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {openBills.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                          Everything is settled.
                        </TableCell>
                      </TableRow>
                    )}
                    {openBills.map((bill) => (
                      <TableRow key={bill.id} hover>
                        <TableCell sx={{ fontWeight: 500 }}>{bill.billNo}</TableCell>
                        <TableCell>{invoiceDate(bill.billDate)}</TableCell>
                        <TableCell align="right">{money(bill.totalAmount)}</TableCell>
                        <TableCell align="right">{money(bill.paidAmount)}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: 'error.main' }}>
                          {money(bill.balanceAmount)}
                        </TableCell>
                        <TableCell align="center">
                          {can('OUTSTANDING', 'create') && (
                            <Tooltip title="Receive payment">
                              <IconButton size="small" color="primary" onClick={() => openPay(bill)} aria-label="Receive payment">
                                <PaymentsIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Payments so far">
                            <IconButton size="small" onClick={() => openHistory(bill)} aria-label="History">
                              <HistoryIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </>
            )}
          </Card>
        </Grid>
      </Grid>

      {/* ------------------------------------------------ receive money */}
      <Dialog open={!!payTarget} onClose={() => setPayTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Typography fontWeight={700} fontSize="1rem">
            Receive Payment
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {payTarget?.billNo} &middot; Balance {money(payTarget?.balanceAmount)}
          </Typography>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            HOW IT IS BEING PAID &mdash; add a second line for part cash, part bank
          </Typography>

          {lines.map((line, index) => (
            <Stack key={index} direction="row" gap={1} alignItems="flex-start" sx={{ mb: 1.5 }}>
              <TextField
                select
                label="Mode"
                size="small"
                sx={{ width: 170 }}
                value={line.paymentMode}
                onChange={(event) => setLine(index, { paymentMode: event.target.value as ReceiptLine['paymentMode'] })}
              >
                <MenuItem value="CASH">Cash</MenuItem>
                <MenuItem value="UPI_CARD">UPI / Card / Bank</MenuItem>
              </TextField>
              <TextField
                label="Amount"
                size="small"
                type="number"
                fullWidth
                autoFocus={index === 0}
                value={line.amount}
                onChange={(event) => setLine(index, { amount: event.target.value })}
              />
              {/* The first line is the payment; the rest can be taken back off. */}
              <Tooltip title={lines.length === 1 ? 'Add a second mode below' : 'Remove this line'}>
                <IconButton
                  size="small"
                  color={lines.length === 1 ? 'primary' : 'default'}
                  onClick={() => (lines.length === 1 ? addLine() : removeLine(index))}
                  sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, width: 40, height: 40, mt: 0.1 }}
                  aria-label={lines.length === 1 ? 'Add another mode' : 'Remove this line'}
                >
                  {lines.length === 1 ? <AddCircleOutlineIcon fontSize="small" /> : <RemoveCircleOutlineIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
            </Stack>
          ))}

          {lines.length > 1 && (
            <Button size="small" startIcon={<AddCircleOutlineIcon />} onClick={addLine} sx={{ mb: 1.5 }}>
              Add another mode
            </Button>
          )}

          {/* What it adds up to, said out loud, so nobody has to do it in their head. */}
          <Card variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: 'action.hover' }}>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2">Receiving now</Typography>
              <Typography variant="body2" fontWeight={700} color={overBalance ? 'error.main' : 'primary.main'}>
                {money(entered)}
              </Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="caption" color="text.secondary">
                {overBalance ? 'More than the bill has left to pay' : 'Left to pay after this'}
              </Typography>
              <Typography variant="caption" color={overBalance ? 'error.main' : 'text.secondary'}>
                {overBalance ? `Balance is only ${money(payTarget?.balanceAmount)}` : money(stillShort)}
              </Typography>
            </Stack>
          </Card>

          <Stack direction="row" gap={2}>
            <TextField
              label="Date"
              size="small"
              type="date"
              sx={{ width: 180 }}
              InputLabelProps={{ shrink: true }}
              value={paymentDate}
              onChange={(event) => setPaymentDate(event.target.value)}
            />
            <TextField label="Remarks" size="small" fullWidth value={remarks} onChange={(event) => setRemarks(event.target.value)} />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button variant="outlined" color="inherit" size="small" onClick={() => setPayTarget(null)}>
            Cancel
          </Button>
          <LoadingButton variant="contained" size="small" loading={receiving} disabled={overBalance} onClick={confirmPay}>
            Receive {money(entered)}
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* ------------------------------------------------ what was paid */}
      <Dialog open={!!historyFor} onClose={() => setHistoryFor(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Typography fontWeight={700} fontSize="1rem">
            Payments against {historyFor?.billNo}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Bill total {money(historyFor?.totalAmount)}
          </Typography>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          {history.length === 0 ? (
            <Alert severity="info">Nothing received yet after the bill was made.</Alert>
          ) : (
            <>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Mode</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Remarks</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {history.map((payment) => (
                    <TableRow key={payment.id}>
                      <TableCell>{payment.paymentDate}</TableCell>
                      <TableCell>
                        <Chip size="small" variant="outlined" label={payment.paymentMode === 'UPI_CARD' ? 'UPI / Card' : 'Cash'} />
                      </TableCell>
                      <TableCell align="right">{money(payment.amount)}</TableCell>
                      <TableCell>{payment.remarks || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* The number the customer asks for: how much have I paid so far? */}
              <Card sx={{ mt: 2, p: 2, bgcolor: 'action.hover' }}>
                <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                  <Typography variant="body2">Paid at the counter when billed</Typography>
                  <Typography variant="body2">
                    {money(Number(historyFor?.paidAmount || 0) - receivedSoFar)}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                  <Typography variant="body2">Received since ({history.length} payment(s))</Typography>
                  <Typography variant="body2">{money(receivedSoFar)}</Typography>
                </Stack>
                <Divider sx={{ my: 1 }} />
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="subtitle2" fontWeight={700}>
                    Total received
                  </Typography>
                  <Typography variant="subtitle2" fontWeight={700}>
                    {money(historyFor?.paidAmount)}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="subtitle2" fontWeight={700}>
                    Balance
                  </Typography>
                  <Typography variant="h6" fontWeight={700} color={Number(historyFor?.balanceAmount) > 0 ? 'error.main' : 'success.main'}>
                    {money(historyFor?.balanceAmount)}
                  </Typography>
                </Stack>
              </Card>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="outlined" color="inherit" size="small" onClick={() => setHistoryFor(null)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Outstanding;
